import { useEffect, useState } from "react"
import { Button, Card, message } from "antd"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { CloudUploadOutlined } from "@ant-design/icons"
import apiClient from "../../../Utils/apiClient"
import PanelLayout from "../../../Layout/PanelLayout"
import TableBox from "../../../components/TableBox/TableBox"
import EmptyState from "../../../components/EmptyState/EmptyState"
import PageSkeleton from "../../../components/PageSkeleton/PageSkeleton"
import { useHeaderContext } from "../../../Utils/HeaderContext"
import { uniqueUpdateUsers } from "../../../Utils/updateCounts"
import { renderDate } from "../../../Utils/utils"
import "../../Operations/Operations.scss"

const InfoRow = ({ label, children }) => (
  <div className="info-grid__row">
    <dt>{label}</dt>
    <dd>{children || "—"}</dd>
  </div>
)

const windowsCommand = (patch) => {
  const updateId = String(patch?.updateId || patch?.Version || "").trim()
  if (updateId) return `windows-update-ids:${updateId}`
  if (patch?.KB) return `windows-update:${String(patch.KB).replace(/^[Kk][Bb]/, "")}`
  if (patch?.Category) return `windows-update-category:${patch.Category}`
  if (patch?.ReleaseDate) return `windows-update-release-dates:${patch.ReleaseDate}`
  return ""
}

const GlobalPatchManagement = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const { state } = useLocation()
  const { setRender, setTaskId } = useHeaderContext()
  const [patchData, setPatchData] = useState(state?.record || null)
  const [loading, setLoading] = useState(!state?.record)
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [updating, setUpdating] = useState(false)

  useEffect(() => {
    const load = async () => {
      if (state?.record) {
        setPatchData(state.record)
        setLoading(false)
        return
      }
      setLoading(true)
      try {
        const data = await apiClient("devices/updates/patches")
        const match = (Array.isArray(data) ? data : []).find(
          (item) => String(item._id) === String(id)
        )
        setPatchData(match || null)
      } catch (error) {
        console.error("Failed to load catalog patch", error)
        setPatchData(null)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [id, state?.record])

  const devices = uniqueUpdateUsers(patchData?.users).map((item) => ({
    ...item,
    key: item.key || `${item.hostname || ""}::${item.user || ""}`,
  }))
  const command = windowsCommand(patchData)

  const queueHosts = async (hostnames) => {
    if (!command) {
      message.warning("This update has no update ID, KB, or category to install")
      return
    }
    setUpdating(true)
    try {
      const appData = [
        {
          description: patchData.Description,
          kb: patchData.KB,
          updateId: patchData.updateId || patchData.Version || "",
          releaseDate: patchData.ReleaseDate,
          title: patchData.Title,
          category: patchData.Category,
        },
      ]
      for (const hostname of hostnames) {
        const response = await apiClient(`devices/${hostname}/create-task`, "POST", {
          entityType: "upgradewindows",
          hostname,
          name: command.split(":")[1] || patchData.Title,
          command,
          appData,
        })
        if (response?.taskId) {
          setTaskId(response.taskId)
          setRender((flag) => !flag)
        }
        message.success(`Queued update for ${hostname}`)
      }
      setSelectedRowKeys([])
    } catch (error) {
      console.error("Failed to queue patch", error)
      message.error("Failed to queue Windows update")
    } finally {
      setUpdating(false)
    }
  }

  if (loading) {
    return (
      <PanelLayout title="Windows update"
      subtitle="Queue a Windows update on selected devices.">
        <PageSkeleton cards={1} rows={6} />
      </PanelLayout>
    )
  }

  if (!patchData) {
    return (
      <PanelLayout title="Windows update"
      subtitle="Queue a Windows update on selected devices.">
        <EmptyState
          title="Update not found"
          description="This catalog entry is no longer pending, or the link is out of date."
          actionLabel="Back to catalog"
          onAction={() => navigate("/global-patches")}
        />
      </PanelLayout>
    )
  }

  return (
    <PanelLayout
      title={patchData.Title || "Windows update"}
      subtitle="Queue this Windows update on selected devices."
      actions={
        <Button
          type="primary"
          icon={<CloudUploadOutlined />}
          loading={updating}
          onClick={() =>
            queueHosts(
              selectedRowKeys.length
                ? devices
                    .filter((item) => selectedRowKeys.includes(item.key))
                    .map((item) => item.hostname)
                : devices.map((item) => item.hostname)
            )
          }
        >
          {selectedRowKeys.length
            ? `Update ${selectedRowKeys.length} selected`
            : `Update ${devices.length} devices`}
        </Button>
      }
    >
      <Card className="info-grid-card ops-card" title="Details" style={{ marginBottom: 16 }}>
        <dl className="info-grid">
          <InfoRow label="KB">{patchData.KB}</InfoRow>
          <InfoRow label="Category">{patchData.Category}</InfoRow>
          <InfoRow label="Released">
            {patchData.ReleaseDate ? renderDate(patchData.ReleaseDate, "DD MMM YYYY") : null}
          </InfoRow>
          <InfoRow label="Vendor">{patchData.Vendor}</InfoRow>
          <InfoRow label="Support">
            {patchData.SupportURL ? (
              <a href={patchData.SupportURL} target="_blank" rel="noreferrer">
                {patchData.SupportURL}
              </a>
            ) : null}
          </InfoRow>
          <InfoRow label="Supersedes">{patchData.Supersedes}</InfoRow>
          <InfoRow label="Description">{patchData.Description}</InfoRow>
        </dl>
      </Card>

      <TableBox
        title="Devices"
        page="Catalog-Windows-devices"
        columns={[
          { title: "Hostname", dataIndex: "hostname", key: "hostname", render: (v) => v || "—" },
          { title: "User", dataIndex: "user", key: "user", render: (v) => v || "—" },
        ]}
        data={devices}
        pagination={{ showSizeChanger: true }}
        rowSelection={{
          selectedRowKeys,
          onChange: setSelectedRowKeys,
        }}
      />
    </PanelLayout>
  )
}

export default GlobalPatchManagement
